import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Checkbox,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Snackbar,
  Alert,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
} from "@mui/material";

import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  getJobsByEmplacement,
  saveJobsSelection,
  createJob,
  updateJob,
} from "../Jobs/api/jobs.api";

import "../../../css/JobSelectorPage.css";
import AddJobDialog from "./modal/AddJobDialog";
import EditJobDialog from "./modal/EditJobDialog";

export default function JobSelectorPage() {
  // ================= Redux =================
  const userState = useSelector((state) => state.user);
  const token = userState.token;
  const emplacementId = userState?.location?.noEmplacement;

  // ================= State =================
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });
  const [saving, setSaving] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  // ================= show message =================
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // ================= Load Jobs =================
  const loadJobs = async () => {
    if (!emplacementId) return;

    try {
      setLoading(true);

      const data = await getJobsByEmplacement(token, emplacementId);

      setJobs(data);

      const selectedIds = data.filter((x) => x.isSelected).map((x) => x.id);

      setSelected(selectedIds);
    } catch (err) {
      console.error(err);
      setErrorSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [emplacementId]);

  // ================= Filter =================
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const text = `${job.nomFR} ${job.nomEN}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [jobs, search]);

  // ================= Toggle =================
  const toggleJob = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAll = () => {
    setSelected(filteredJobs.map((j) => j.id));
  };

  const unselectAll = () => {
    setSelected([]);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    try {
      setSaving(true);

      const response = await saveJobsSelection(token, emplacementId, selected);

      if (response.success) {
        showSnackbar(
          response.messageFr || "Jobs saved successfully",
          "success",
        );

        await loadJobs();
      } else {
        showSnackbar(response.messageFr || "Error while saving jobs", "error");
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Server error", "error");
    } finally {
      setSaving(false);
    }
  };

  // ================= ACTIONS =================
  // ================= EDIT =================
  const handleEdit = (job) => {
    setSelectedJob(job);
    setOpenEdit(true);
  };

  const handleUpdateJob = async (job, form) => {
    try {
      const res = await updateJob(token, job.id, form);

      if (res.success) {
        showSnackbar(res.messageFr || "Job updated successfully", "success");

        await loadJobs(); // 🔥 refresh backend
        setOpenEdit(false);
        setSelectedJob(null);
      } else {
        showSnackbar(res.messageFr || "Error updating job", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Server error", "error");
    }
  };

  const handleDelete = (job) => {
    console.log("Delete job:", job);
  };

  // ================= ADD =================
  const handleOpenAdd = () => {
    setOpenAdd(true);
  };

  const handleAddJob = async (form) => {
    try {
      const res = await createJob(token, form);

      if (res.success) {
        showSnackbar(res.messageFr || "Job created successfully", "success");

        await loadJobs();
        setOpenAdd(false);
      } else {
        showSnackbar(res.messageFr || "Error creating job", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Server error", "error");
    }
  };

  // ================= UI =================
  return (
    <Box sx={{ p: 3, pb: 10 }}>
      {/* ================= HEADER ================= */}
      <Card
        className="mh-header-card"
        sx={{
          mb: 3,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <Box className="mh-header-topbar" />

        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Restaurant Jobs
              </Typography>

              <Typography variant="body1" color="text.secondary">
                Manage jobs available for this restaurant location
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                👉 Id: {userState?.location?.noEmplacement}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                📍 {userState?.location?.nomEmplacement}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {jobs.length} jobs available
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ borderRadius: 3 }}
              onClick={handleOpenAdd}
            >
              Add Job
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ================= SEARCH ================= */}
      <Grid container sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Search jobs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* ================= ACTIONS ================= */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={selectAll}>
          Select All
        </Button>

        <Button variant="outlined" onClick={unselectAll}>
          Unselect All
        </Button>
      </Stack>

      {/* ================= LIST ================= */}
      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Typography>Loading jobs...</Typography>
          </Box>
        ) : (
          <List>
            {filteredJobs.map((job) => (
              <ListItem
                key={job.id}
                divider
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    backgroundColor: "#f5f7fb",
                  },
                }}
              >
                {/* LEFT SIDE */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar sx={{ bgcolor: "#1976d2" }}>
                    <WorkOutlineIcon />
                  </Avatar>

                  <Box>
                    <Typography fontWeight={600}>{job.nomFR}</Typography>

                    <Typography variant="body2" color="text.secondary">
                      {job.nomEN}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      ID: {job.id}
                    </Typography>
                  </Box>
                </Box>

                {/* RIGHT SIDE (NEW ACTIONS + CHECKBOX) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selected.includes(job.id)}
                    onChange={() => toggleJob(job.id)}
                  />

                  <Tooltip title="Edit Job">
                    <IconButton size="small" onClick={() => handleEdit(job)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete Job">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(job)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      {/* ================= SAVE BAR ================= */}
      <AppBar
        position="fixed"
        sx={{
          top: "auto",
          bottom: 0,
          background: "#fff",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2">
              {selected.length} job(s) selected
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !emplacementId}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Toolbar>
      </AppBar>

      {/* ================= modal add/edit ================= */}
      <AddJobDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSave={handleAddJob}
      />
      <EditJobDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        job={selectedJob}
        onUpdate={handleUpdateJob}
      />

      {/* ================= SNACKBARS ================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
